import React, { useCallback, useMemo, useRef } from 'react';
import DataGrid, { Column, FormatterProps } from 'react-data-grid';
import { isFunction } from 'lodash';
import { config } from 'config';
import clsx from 'clsx';
import { TEST_ID_LOADING_INDICATOR } from 'app/utils/test/constants';
import { Box, Skeleton, useStyleConfig } from '@chakra-ui/react';
import { classes } from 'app/chakra-theme/components/TableList';
import { MappedColumns } from 'app/components/ChakraTableList/MappedColumns';
import { RowRenderer } from 'app/components/ChakraTableList/RowRenderer';
import { rowHeights } from 'app/components/ChakraTableList/constants';
import { BodyRow, HeaderRow, RowType, TableListProps } from './types';
import {
  areComponentPropsEqual,
  getRowHeight,
  getRowsData,
  getVariant,
  isAtBottom,
} from './utils';
import { ColumnFormatter } from './ColumnFormatter';
import { useContextMenu, useListContainerHeight, useRows } from './hooks';
import { ContextMenu } from './ContextMenu';

const SubHeaderLoader = () => <Skeleton width="100%" height={6} />;

const TableListComponent: React.FC<TableListProps> = ({
  rows: rowsProp,
  columns,
  summaryRows,
  loadingOptions,
  onRowClick,
  rowRenderer: customRowRenderer,
  className,
  regularRowHeight = rowHeights.regular,
  scrollable,
  'data-testid': dataTestId,
  memoizeColumnFormatter,
  rowKeyGetter,
  onRowsChange,
  dragCorrectionDistance,
  fetchMore,
  hasMore,
  id,
  wrapperSx,
  contextMenuProps: passedContextMenuProps,
  variant: variantProp,
}) => {
  const toggledRows = useRef({});
  const { rows, toggleRow } = useRows(rowsProp, toggledRows);
  const contextMenuProps = useContextMenu(passedContextMenuProps);
  const { setOptions, setPosition } = contextMenuProps;
  const { loading = false, loadingMore = false } = loadingOptions || {};
  const noHeader = !columns.find((column) => column.name !== '');

  const rowRenderer = useCallback(
    (props) => (
      <RowRenderer
        customRowRenderer={customRowRenderer}
        setOptions={setOptions}
        setPosition={setPosition}
        {...props}
      />
    ),
    [customRowRenderer, setOptions, setPosition]
  );

  const columnFormatter = ({
    row,
    column,
    ...props
  }: FormatterProps<BodyRow>) => (
    <ColumnFormatter
      {...props}
      column={column}
      row={row}
      onCollapse={toggleRow}
      isSectionCollapsed={!toggledRows.current[row.id as string]}
      listItemClassName={clsx({
        [classes.rightAlignEllipsis]: ((column as unknown) as HeaderRow)
          ?.alignRight,
      })}
      contextMenuProps={contextMenuProps}
    />
  );

  const memoizedColumnFormatter = useMemo(
    () => columnFormatter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columnsData = useMemo(
    () =>
      MappedColumns({
        columns,
        formatter: memoizeColumnFormatter
          ? memoizedColumnFormatter
          : columnFormatter,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns]
  );

  const listHeight = useListContainerHeight({
    loadingOptions,
    rows,
    noHeader,
    summaryRows: summaryRows?.length,
    dragCorrectionDistance,
    regularRowHeight,
  });

  const handleScroll = async (event: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMore || !hasMore || !isAtBottom(event)) return;

    if (isFunction(fetchMore)) {
      await fetchMore();
    }
  };

  const variant = getVariant(loading, scrollable, variantProp);

  const styles = useStyleConfig('TableList', { variant });

  return (
    <Box __css={styles} height={`${listHeight}px`} id={id} sx={wrapperSx}>
      <ContextMenu contextMenuProps={contextMenuProps} />
      <DataGrid
        defaultColumnOptions={{ minWidth: 32 }}
        data-testid={
          loading || loadingMore ? TEST_ID_LOADING_INDICATOR : dataTestId
        }
        enableVirtualization={config.env !== 'test'}
        columns={columnsData as Column<HeaderRow>[]}
        rows={getRowsData(rows, columns, <SubHeaderLoader />, loadingOptions)}
        summaryRowHeight={rowHeights.regular}
        summaryRows={summaryRows && loading ? [{}] : summaryRows}
        rowHeight={({ row }) => getRowHeight(row as BodyRow, regularRowHeight)}
        headerRowHeight={noHeader ? 0 : undefined}
        rowKeyGetter={rowKeyGetter}
        rowClass={(row) => {
          return clsx(row.className, classes.row, {
            [classes.rowClickable]: Boolean(row.to) || Boolean(row.clickable),
            [classes.rowDisabled]: Boolean(row.disabled),
            [classes.subHeader]: row.type === RowType.SUBHEADER,
            [classes.subHeaderLoader]: row.type === RowType.SUBHEADER_LOADER,
            [classes.hideBorder]: row.hideBorder,
            [classes.customContentRow]: row.type === RowType.CUSTOM_CONTENT,
          });
        }}
        rowRenderer={rowRenderer}
        onRowClick={(row) => {
          if (onRowClick && !loading) {
            onRowClick(row);
          }
        }}
        onRowsChange={onRowsChange}
        className={className}
        onScroll={handleScroll}
      />
    </Box>
  );
};

export const TableList = React.memo(TableListComponent, areComponentPropsEqual);
